/**
 * Hook customizado para WebSocket
 * Gerencia conexão e recebimento de mensagens em tempo real
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { 
  WebSocketService, 
  WebSocketMessage, 
  webSocketManager 
} from '../../data/services/WebSocketService';

interface UseWebSocketOptions {
  autoConnect?: boolean;
  onMessage?: (message: WebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: any) => void;
}

interface UseWebSocketReturn {
  isConnected: boolean;
  lastMessage: WebSocketMessage | null;
  connect: () => void;
  disconnect: () => void;
  error: string | null;
}

/**
 * Hook para gerenciar conexão WebSocket
 * 
 * @param url - URL do WebSocket (ex: ws://localhost:8090/ws/live)
 * @param options - Opções de configuração
 * @returns Estado e funções de controle do WebSocket
 * 
 * @example
 * ```tsx
 * const { isConnected, lastMessage } = useWebSocket('ws://localhost:8090/ws/live', {
 *   autoConnect: true,
 *   onMessage: (message) => {
 *     if (message.type === 'detection') {
 *       console.log('Nova detecção:', message.data);
 *     }
 *   }
 * });
 * ```
 */
export function useWebSocket(
  url: string,
  options: UseWebSocketOptions = {}
): UseWebSocketReturn {
  const {
    autoConnect = true,
    onMessage,
    onConnect,
    onDisconnect,
    onError,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const serviceRef = useRef<WebSocketService | null>(null);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Inicializar serviço
  useEffect(() => {
    serviceRef.current = webSocketManager.getService(url);
    return () => {
      if (serviceRef.current) {
        serviceRef.current.disconnect();
      }
    };
  }, [url]);

  // Handler de mensagens
  const handleMessage = useCallback((message: WebSocketMessage) => {
    setLastMessage(message);

    // Atualizar estado de conexão
    if (message.type === 'camera_status' && message.data.status === 'connected') {
      setIsConnected(true);
      setError(null);
      onConnect?.();
    } else if (message.type === 'error') {
      setError(message.data.message);
      onError?.(message.data);
    }

    // Notificar callback
    onMessage?.(message);
  }, [onMessage, onConnect, onError]);

  // Registrar callback
  useEffect(() => {
    if (!serviceRef.current) return;

    const unsubscribe = serviceRef.current.addCallback(handleMessage);
    return unsubscribe;
  }, [handleMessage]);

  // Conectar automaticamente
  useEffect(() => {
    if (autoConnect && serviceRef.current) {
      connect();
    }

    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
    };
  }, [autoConnect, url]);

  // Monitorar estado de conexão
  useEffect(() => {
    const checkConnection = setInterval(() => {
      if (serviceRef.current) {
        const connected = serviceRef.current.isConnected();
        if (connected !== isConnected) {
          setIsConnected(connected);
          if (!connected) {
            onDisconnect?.();
          }
        }
      }
    }, 1000);

    return () => clearInterval(checkConnection);
  }, [isConnected, onDisconnect]);

  const connect = useCallback(() => {
    if (serviceRef.current) {
      serviceRef.current.connect();
    }
  }, []);

  const disconnect = useCallback(() => {
    if (serviceRef.current) {
      serviceRef.current.disconnect();
      setIsConnected(false);
      onDisconnect?.();
    }
  }, [onDisconnect]);

  return {
    isConnected,
    lastMessage,
    connect,
    disconnect,
    error,
  };
}
